import { Button } from 'common/components/Button';
import { EmployeeProfileData } from 'common/types/employee';

import { EmployeeProfileHero } from '../EmployeeProfileHero';

const SectionTitle = ({ eyebrow, title }: { eyebrow?: string; title: string }) => (
  <div className="max-w-[760px]">
    <div className="mb-4 flex items-center gap-3">
      <span className="h-px w-10 bg-primary" />
      {!!eyebrow && <div className="font-semibold uppercase text-primary text-xs">{eyebrow}</div>}
    </div>
    <h2
      className="text-heading-about font-black text-white mb-2"
      dangerouslySetInnerHTML={{ __html: title }}
    />
  </div>
);

export const EmployeeProfile: React.FC<{ employee: EmployeeProfileData }> = ({ employee }) => {
  const renderCta = () => (
    <section>
      <div className="container py-12 md:py-16 lg:py-20 border-t border-b border-white/20">
        <SectionTitle title={employee.dualCta.title} />
        <div className="grid gap-5 lg:grid-cols-2 mt-6">
          <div className="rounded-[28px] border border-primary/20 bg-primary/[0.08] p-6 lg:p-8">
            <div>
              <div className="font-semibold uppercase text-primary">
                {employee.dualCta.person.label}
              </div>
              <h3 className="text-3xl font-black leading-tight text-white">
                {employee.dualCta.person.title}
              </h3>
              <p className="mt-5 max-w-[54ch] leading-8 text-white/78">
                {employee.dualCta.person.text}
              </p>
              <p className="mt-4 italic">{employee.dualCta.person.microcopy}</p>
            </div>
            <div className="mt-8 max-w-[320px]">
              <Button href={employee.consultingCtaHref} rel="noopener noreferrer" target="_blank">
                {employee.dualCta.person.cta}
              </Button>
            </div>
          </div>
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 lg:p-8 flex flex-col justify-between">
            <div>
              <div className="font-semibold uppercase text-primary">
                {employee.dualCta.company.label}
              </div>
              <h3 className="text-3xl font-black leading-tight text-white">
                {employee.dualCta.company.title}
              </h3>
              <p className="mt-5 max-w-[54ch]">{employee.dualCta.company.text}</p>
              <p className="mt-4 italic">{employee.dualCta.company.microcopy}</p>
            </div>
            <div className="mt-8 max-w-[320px]">
              <Button href={employee.companyCtaHref} type="outline">
                {employee.dualCta.company.cta}
              </Button>
            </div>
          </div>
        </div>
        <p className="mt-2 ml-4 text-white">{employee.dualCta.helper}</p>
      </div>
    </section>
  );

  return (
    <div className="bg-gradient text-white">
      <EmployeeProfileHero employee={employee} />

      <section className="pb-12 md:pb-14 lg:pb-16 z-10 relative mt-[-100px]">
        <div className="container">
          <div className="rounded-[28px] shadow shadow-white/20 bg-gradient p-6 md:p-12 lg:px-20">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-primary" />
              <div className="font-semibold uppercase text-primary text-xs">
                {employee.trust.title}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 lg:gap-3">
              {employee.trust.companies.map(company => (
                <div
                  key={company}
                  className="rounded-full border border-white/12 bg-[#140f1d] px-4 py-4 lg:px-8 lg:py-5">
                  {company}
                </div>
              ))}
            </div>
            <p className="max-w-[62ch] mt-4">{employee.trust.intro}</p>
          </div>
        </div>

        <div className="container grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] pt-12 lg:pt-32">
          <div>
            <SectionTitle title={employee.about.title} />
          </div>
          <div className="space-y-6">
            {employee.about.paragraphs.map(paragraph => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14 lg:py-16">
        <div className="container">
          <SectionTitle title={employee.experienceContext.title} />
          <p className="max-w-[62ch]">{employee.experienceContext.intro}</p>
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            {employee.experienceContext.items.map(item => (
              <article
                key={item.title}
                className="rounded-[26px] border border-white/10 bg-white/5 p-6">
                <h3 className="text-2xl font-bold text-primary">{item.title}</h3>
                <p className="mt-2">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14 lg:py-16">
        <div className="container">
          <SectionTitle title={employee.whatIDo.title} />
          {!!employee.whatIDo.subtitle && (
            <p className="max-w-[64ch]">{employee.whatIDo.subtitle}</p>
          )}
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {employee.whatIDo.items.map(item => (
              <article
                key={item.title}
                className="rounded-[26px] border border-white/10 bg-white/5 p-6">
                <h3 className="text-2xl font-bold text-primary">{item.title}</h3>
                <p className="mt-2">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14 lg:py-16">
        <div className="container">
          <SectionTitle title={employee.projects.title} />
          <p className="max-w-[62ch]">{employee.projects.intro}</p>
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {employee.projects.items.map(project => (
              <article
                key={project.title}
                className="rounded-[26px] border border-white/10 bg-white/5 p-6">
                <div className="font-semibold uppercase text-primary">{project.label}</div>
                <h3 className="text-3xl font-black leading-tight text-white">{project.title}</h3>
                <p className="mt-2">{project.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="employee-section py-12 md:py-14 lg:py-16">
        <div className="container">
          <SectionTitle title={employee.experiences.title} />
          <p className="employee-section-copy max-w-[62ch]">{employee.experiences.intro}</p>
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {employee.experiences.items.map(item => (
              <article
                key={item.title}
                className="employee-reveal-card rounded-[26px] border border-white/10 bg-white/5 p-6">
                <h3 className="text-2xl font-bold text-primary">{item.title}</h3>
                <p className="mt-2">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-14 lg:py-16">
        <div className="container grid gap-10 lg:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)]">
          <div>
            <SectionTitle title={employee.approach.title} />
            <p className="max-w-[60ch]">{employee.approach.intro}</p>
          </div>
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 lg:p-8">
            <ul className="space-y-2">
              {employee.approach.principles.map(item => (
                <li key={item} className="flex gap-4">
                  <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 border-t border-white/10 pt-6 italic font-semibold text-xl">
              {employee.approach.closing}
            </p>
          </div>
        </div>
      </section>

      {renderCta()}
    </div>
  );
};
